import { useEffect } from "react";
import { Switch, Route, Router as WouterRouter, Redirect } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { setupFetchInterceptor } from "./lib/fetch-interceptor";
// Pages
import NotFound from "@/pages/not-found";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import PublicHandle, { BranchPage } from "@/pages/PublicHandle";
import { UseCasePage } from "@/pages/UseCasePage";
import DemoProfiles from "@/pages/DemoProfiles";
import DemoProfileViewer from "@/pages/DemoProfileViewer";
import UseCasesGallery from "@/pages/UseCasesGallery";
import UseCaseDetailPage from "@/pages/UseCaseDetailPage";
import DualSecretScan from "@/pages/DualSecretScan";
import GoogleAuthSuccess from "@/pages/GoogleAuthSuccess";
import SetupUsername from "@/pages/SetupUsername";
import Admin from "@/pages/Admin";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import { CookieBanner } from "@/components/ui/CookieBanner";
import TermsOfService from "@/pages/TermsOfService";
import Themes from "@/pages/Themes";

// Initialize Query Client
const queryClient = new QueryClient();
// Run interceptor setup once
setupFetchInterceptor();

// All 18 detail page slugs (must come before /:username catch-all)
const WHO_SLUGS = [
  "brands",
  "businesses",
  "products",
  "local-businesses",
  "celebrities",
  "professionals",
];
const USE_CASE_SLUGS = [
  "business-card",
  "product-packaging",
  "storefront",
  "restaurant-menu",
  "event-pass",
  "social-hub",
  "ad-campaign",
  "commercial-invoice",
  "highway-hoarding",
  "retail-carry-bag",
  "office-envelope",
  "ecommerce-parcel",
];
const ALL_DETAIL_SLUGS = [...WHO_SLUGS, ...USE_CASE_SLUGS];

function Router() {
  return (
    <Switch>
      {/* ── Static platform routes ── */}
      <Route path="/" component={Landing} />
      <Route path="/login" component={Login} />
      <Route path="/signup" component={Signup} />
      <Route path="/register" component={Signup} />
      <Route path="/themes" component={Themes} />
      <Route path="/dashboard" component={Dashboard} />
      <Route path="/admin" component={Admin} />
      <Route path="/setup-username" component={SetupUsername} />

      {/* ── Auth flows ── */}
      <Route path="/auth/google/success" component={GoogleAuthSuccess} />
      <Route path="/verify" component={VerifyEmail} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/reset-password" component={ResetPassword} />

      {/* ── Demo pages ── */}
      <Route path="/demos" component={DemoProfiles} />
      <Route path="/demos/:slug" component={DemoProfileViewer} />

      {/* ── Use cases — must come before /:username ── */}
      <Route path="/use-cases" component={UseCasesGallery} />
      <Route path="/use-cases/:slug" component={UseCaseDetailPage} />

      {/* ── 18 detail pages — must come before /:username ── */}
      {ALL_DETAIL_SLUGS.map((slug) => (
        <Route key={slug} path={`/${slug}`}>
          {() => <UseCasePage slug={slug} />}
        </Route>
      ))}

      {/* ── DualSecret™ token scan — must come before /:username ── */}
      <Route path="/org/:orgSlug/:serialCode" component={DualSecretScan} />

      {/* ── Legal pages — must come before /:username ── */}
      <Route path="/privacy" component={PrivacyPolicy} />
      <Route path="/terms" component={TermsOfService} />

      {/* ── Explicit 404 route — must come before /:username ── */}
      <Route path="/404" component={NotFound} />

      {/* Public resolver routes follow every fixed platform route. */}
      <Route path="/:handle/:branchSlug" component={BranchPage} />
      <Route path="/:handle" component={PublicHandle} />

      {/* ── Catch-all — redirects to branded 404 ── */}
      <Route>{() => <Redirect to="/404" />}</Route>
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
          <Router />
        </WouterRouter>
        <Toaster />
        <CookieBanner />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
